// src/components/filters/TickerPresets.jsx
import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Paper,
  InputBase,
  IconButton,
  Popper,
  Typography,
  ClickAwayListener,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import CloseIcon from "@mui/icons-material/Close";
import { useTheme } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import { useAuth0 } from "@auth0/auth0-react";
import { trackEvent } from '../../utils/analytics';
import { getControlTokens } from "../common/controls";
import {
  saveList,
  deleteList,
} from "../../features/favorites/favoritesSlice";
import { buildTickerSelection } from "../../utils/industryDatasetSelections";

function TickerPresets({
  selectedTickers,
  setSelectedTickers,
  allTickers,
  isAuthenticated: isAuthenticatedProp,
  leftBorderOnly = false,
  controlContext = 'filterBar',
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [newFavoriteName, setNewFavoriteName] = useState("");
  const [activeFavorite, setActiveFavorite] = useState(null);
  const [showSignupDialog, setShowSignupDialog] = useState(false);

  const theme = useTheme();
  const tokens = getControlTokens(theme, controlContext);
  const inputRef = useRef(null);
  const newFavoriteInputRef = useRef(null);

  // Get ticker favorites from Redux
  const favorites = useSelector((state) => state.favorites.lists || {});
  const dispatch = useDispatch();
  const { getAccessTokenSilently, isAuthenticated: authIsAuthenticated, loginWithRedirect } = useAuth0();

  // Use prop if provided (for dev mode override), otherwise use Auth0
  const isAuthenticated = isAuthenticatedProp !== undefined ? isAuthenticatedProp : authIsAuthenticated;

  // Handle opening/closing the menu
  const handleToggle = (event) => {
    if (!isAuthenticated) {
      setShowSignupDialog(true);
      return;
    }
    const isClosing = Boolean(anchorEl);
    setAnchorEl(isClosing ? null : event.currentTarget);
  };
  const open = Boolean(anchorEl);

  // Autofocus new favorite input when the menu opens
  useEffect(() => {
    if (anchorEl && newFavoriteInputRef.current) {
      setTimeout(() => newFavoriteInputRef.current?.focus(), 100);
    }
  }, [anchorEl]);

  // Helper: compare two arrays of tickers
  const isFavoriteChanged = (name) => {
    const original = buildTickerSelection(favorites[name] || [], allTickers).map((t) => t.ticker).sort();
    const current = buildTickerSelection(selectedTickers, allTickers).map((t) => t.ticker).sort();
    if (original.length !== current.length) return true;
    return !original.every((val, idx) => val === current[idx]);
  };

  // Save a new favorite
  const saveNewFavorite = async () => {
    const name = newFavoriteName.trim();
    if (!name || selectedTickers.length === 0) return;

    try {
      const token = await getAccessTokenSilently({ audience: "https://api.softwareiq.io" });
      await dispatch(
        saveList({ token, listName: name, items: selectedTickers })
      ).unwrap();
      trackEvent('preset_change', { preset_type: 'ticker', operation: 'create', result_count: selectedTickers.length });
      setNewFavoriteName("");
      setActiveFavorite(name);
    } catch (err) {
      console.error("Error saving ticker favorite:", err);
    }
  };

  // Save changes to an existing favorite
  const saveExistingFavorite = async (name) => {
    if (!name) return;
    try {
      const token = await getAccessTokenSilently({ audience: "https://api.softwareiq.io" });
      await dispatch(
        saveList({ token, listName: name, items: selectedTickers })
      ).unwrap();
      trackEvent('preset_change', { preset_type: 'ticker', operation: 'update' });
    } catch (err) {
      console.error("Error updating ticker favorite:", err);
    }
  };

  // Load a favorite into selectedTickers
  const loadFavorite = (name) => {
    const items = favorites[name] || [];
    setSelectedTickers(buildTickerSelection(items, allTickers));
    setActiveFavorite(name);
  };

  // Delete a favorite
  const handleDeleteFavorite = async (name) => {
    try {
      const token = await getAccessTokenSilently({ audience: "https://api.softwareiq.io" });
      await dispatch(deleteList({ token, listName: name })).unwrap();
      trackEvent('preset_change', { preset_type: 'ticker', operation: 'delete' });
      if (name === activeFavorite) {
        setActiveFavorite(null);
      }
    } catch (err) {
      console.error("Error deleting ticker favorite:", err);
    }
  };

  // Signup handlers
  const handleSignupClick = () => {
    localStorage.setItem('redirectTo', window.location.pathname);
    localStorage.setItem("requestedTier", "free");
    loginWithRedirect({
      screen_hint: "signup",
      appState: { requestedTier: "free" },
      authorizationParams: { screen_hint: "signup", scope: "openid email profile" }
    });
  };

  const handleLoginClick = () => {
    localStorage.setItem('redirectTo', window.location.pathname);
    loginWithRedirect();
  };

  const selectBorderColor = tokens.borderColor;

  const hasFavorites = Object.keys(favorites).length > 0;

  return (
    <Box>
      {/* Favorite icon button */}
      <Tooltip title="Company Favorites">
        <IconButton
          ref={inputRef}
          onClick={handleToggle}
          size="small"
          sx={{
            backgroundColor: tokens.surface,
            height: tokens.height,
            width: tokens.height,
            color: activeFavorite
              ? theme.palette.primary.main
              : theme.palette.text.secondary,
            ...(leftBorderOnly
              ? {
                  border: 'none',
                  borderLeft: `1px solid ${selectBorderColor}`,
                  borderRadius: 0,
                }
              : {
                  border: `1px solid ${selectBorderColor}`,
                  borderRadius: `${tokens.radius}px`,
                }
            ),
            '& .MuiSvgIcon-root': {
              fontSize: tokens.iconSize,
            },
          }}
        >
          {activeFavorite ? (
            <BookmarkIcon fontSize="small" />
          ) : (
            <BookmarkBorderIcon fontSize="small" />
          )}
        </IconButton>
      </Tooltip>

      {/* Signup Dialog for unauthenticated users */}
      <Dialog
        open={showSignupDialog}
        onClose={() => setShowSignupDialog(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            p: 1.5,
            borderRadius: 3,
            boxShadow: theme.shadows[8],
            backdropFilter: "blur(6px)",
            backgroundColor: theme.background.transparent,
          }
        }}
      >
        <Box sx={{ position: "absolute", top: 8, right: 8 }}>
          <IconButton onClick={() => setShowSignupDialog(false)} size="small" aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <DialogTitle sx={{ fontWeight: 700, pr: 5 }}>
          Save Your Favorite Companies
        </DialogTitle>

        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Create a free account to save and manage your favorite company lists.
          </Typography>

          <Box sx={{
            p: 1.5,
            borderRadius: 2,
            bgcolor: theme.palette.mode === "dark" ? "rgba(80,100,255,0.12)" : "rgba(80,100,255,0.10)",
            border: `1px dashed ${theme.palette.primary.main}`,
            fontSize: 12,
          }}>
            Sign up today to save custom company lists, quickly filter by your favorites, and access them across all pages.
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleLoginClick} variant="text">Login</Button>
          <Button onClick={handleSignupClick} variant="contained">Create Free Account</Button>
        </DialogActions>
      </Dialog>

      {/* Popper menu - only shown when authenticated */}
      <Popper open={open} anchorEl={anchorEl} placement="bottom-start" sx={{ zIndex: 1300 }}>
        <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
          <Paper
            sx={{
              p: 2,
              mt: 1,
              width: 280,
              maxWidth: 280,
              maxHeight: 400,
              overflowY: "auto",
              backgroundColor: theme.background.transparent,
              backdropFilter: "blur(6px)",
              color: theme.palette.text.secondary,
            }}
          >
            {/* Favorites heading */}
            <Typography variant="subtitle2" fontSize={13} mb={1}>
              Company Favorites
            </Typography>

            {/* List of saved favorites */}
            {!hasFavorites ? (
              <Typography fontSize={11} color="text.secondary" mb={1}>
                No saved favorites
              </Typography>
            ) : (
              Object.keys(favorites).map((name) => {
                const changed = activeFavorite === name && isFavoriteChanged(name);
                const isActive = name === activeFavorite;
                return (
                  <Box
                    key={name}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 0.5,
                      backgroundColor: isActive
                        ? theme.tableHeader.highlightDark
                        : "transparent",
                      borderRadius: 1,
                      px: 0.5,
                      py: 0.25,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        flexGrow: 1,
                        overflow: "hidden",
                      }}
                    >
                      <Typography
                        fontSize={13}
                        sx={{
                          cursor: "pointer",
                          color: theme.palette.primary.main,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          ...(isActive
                            ? { fontWeight: "bold", textDecoration: "underline" }
                            : {}),
                        }}
                        onClick={() => loadFavorite(name)}
                      >
                        {name}
                      </Typography>

                      {changed && (
                        <Tooltip title="Save changes">
                          <IconButton
                            onClick={() => saveExistingFavorite(name)}
                            size="small"
                            sx={{ ml: 0.5 }}
                          >
                            <SaveIcon
                              fontSize="small"
                              sx={{ color: theme.palette.success.main, fontSize: "18px" }}
                            />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>

                    <Tooltip title="Delete favorite">
                      <IconButton
                        onClick={() => handleDeleteFavorite(name)}
                        size="small"
                        sx={{ ml: 1 }}
                      >
                        <DeleteIcon
                          fontSize="small"
                          sx={{ color: theme.palette.text.secondary, fontSize: "18px" }}
                        />
                      </IconButton>
                    </Tooltip>
                  </Box>
                );
              })
            )}

            {/* Add new favorite */}
            <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
              <InputBase
                inputRef={newFavoriteInputRef}
                placeholder="New favorite name"
                value={newFavoriteName}
                onChange={(e) => setNewFavoriteName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    saveNewFavorite();
                  }
                }}
                sx={{
                  backgroundColor: theme.tableHeader.highlight,
                  fontSize: 12,
                  px: 1,
                  py: 0.5,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  flexGrow: 1,
                }}
              />
              <Tooltip title="Save current companies as favorite">
                <IconButton
                  onClick={saveNewFavorite}
                  size="small"
                  disabled={!newFavoriteName.trim() || selectedTickers.length === 0}
                  sx={{ color: theme.palette.success.main }}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Company count info */}
            <Typography fontSize={10} color="text.secondary" mt={1}>
              Current selection: {selectedTickers.length} companies
            </Typography>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
}

export default TickerPresets;
